import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, X, Image as ImageIcon, File as FileIcon, Video } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ChatInput = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((message.trim() || attachments.length > 0) && !disabled && !uploading) {
      onSend(message, attachments);
      setMessage('');
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadedFiles = [];

      for (const file of files) {
        // Check file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name} est trop grand. Taille maximale: 10MB`);
          continue;
        }

        // Upload file
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Erreur d'upload: ${file.name}`);
        }

        const data = await response.json();
        uploadedFiles.push({
          file_id: data.file_id,
          filename: data.filename,
          content_type: data.content_type,
          size: data.size,
          data: data.data,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        });
      }

      setAttachments(prev => [...prev, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Erreur lors de l\'upload des fichiers');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments(prev => {
      const updated = prev.filter(att => att.file_id !== fileId);
      // Revoke object URLs for images
      const removed = prev.find(att => att.file_id === fileId);
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) return <ImageIcon size={16} />;
    if (contentType.startsWith('video/')) return <Video size={16} />;
    return <FileIcon size={16} />;
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [message]);

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 z-40 pb-3 sm:pb-4 pt-6"
      style={{
        background: 'linear-gradient(to top, var(--bg-base) 60%, transparent)'
      }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <div className="max-w-3xl mx-auto px-3 sm:px-4">
        {/* Attachments preview */}
        <AnimatePresence>
          {attachments.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mb-2 flex flex-wrap gap-2"
            >
              {attachments.map((att) => (
                <motion.div
                  key={att.file_id}
                  layout
                  className="relative rounded-lg border overflow-hidden"
                  style={{
                    borderColor: 'var(--line)',
                    background: 'var(--bg-elev-1)'
                  }}
                >
                  {att.preview ? (
                    <img
                      src={att.preview}
                      alt={att.filename}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center" style={{ color: 'var(--accent-cyan)' }}>
                      {getFileIcon(att.content_type)}
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(att.file_id)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                  >
                    <X size={12} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5">
                    <p className="text-xs text-white truncate" title={att.filename}>
                      {att.filename}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-1.5 sm:gap-2 rounded-2xl border backdrop-blur-xl p-1.5 sm:p-2"
          style={{
            borderColor: 'rgba(var(--line), 0.6)',
            background: 'rgba(var(--bg-elev-1), 0.8)'
          }}
        >
          {/* File attachment button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            className="flex-shrink-0 h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center transition-all"
            style={{
              background: disabled || uploading ? 'var(--text-muted)' : 'var(--bg-elev-2)',
              color: disabled || uploading ? '#666' : 'var(--accent-ocean)',
              cursor: disabled || uploading ? 'not-allowed' : 'pointer',
              opacity: disabled || uploading ? 0.5 : 1
            }}
            data-testid="attach-file-button"
          >
            {uploading ? (
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Paperclip size={18} className="sm:hidden" />
            )}
            {!uploading && <Paperclip size={20} className="hidden sm:block" />}
          </button>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Lucy..."
            disabled={disabled || uploading}
            className="flex-1 resize-none bg-transparent font-ui text-sm md:text-base focus:outline-none min-h-[40px] sm:min-h-[48px] max-h-40 py-2.5 sm:py-3 px-2"
            style={{
              color: 'var(--text-primary)'
            }}
            rows={1}
            data-testid="chat-input-textarea"
          />
          <button
            type="submit"
            disabled={disabled || uploading || (!message.trim() && attachments.length === 0)}
            className="flex-shrink-0 h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center transition-all"
            style={{
              background: disabled || uploading || (!message.trim() && attachments.length === 0) ? 'var(--text-muted)' : 'var(--accent-cyan)',
              color: '#0b1220',
              cursor: disabled || uploading || (!message.trim() && attachments.length === 0) ? 'not-allowed' : 'pointer',
              opacity: disabled || uploading || (!message.trim() && attachments.length === 0) ? 0.5 : 1
            }}
            data-testid="chat-input-send-button"
          >
            <Send size={18} className="sm:hidden" />
            <Send size={20} className="hidden sm:block" />
          </button>
        </form>
        <p className="text-xs text-center mt-1.5 sm:mt-2 px-2" style={{ color: 'var(--text-muted)' }}>
          {uploading ? 'Upload en cours...' : 'Enter pour envoyer, Shift+Enter pour une nouvelle ligne'}
        </p>
      </div>
    </motion.div>
  );
};

export default ChatInput;
